/**
 * theme.tsx — dark/light theming, mirroring i18n.tsx. A `ThemeProvider` holds the
 * active theme and reflects it onto `<html data-theme>`, which the CSS token blocks
 * in `styles/app.css` key off. The initial value is resolved *before* React paints
 * by the inline script in `index.html` (saved choice → system preference → dark);
 * here we simply adopt that already-applied attribute so the two never disagree.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Icon } from "./format.tsx";

export type Theme = "dark" | "light";
const KEY = "lineage-theme";

function readInitial(): Theme {
  // The pre-paint script already set data-theme; adopt it as the source of truth.
  if (typeof document !== "undefined") {
    const attr = document.documentElement.dataset.theme;
    if (attr === "light" || attr === "dark") return attr;
  }
  return "dark";
}

interface Ctx {
  theme: Theme;
  setTheme: (t: Theme) => void;
  toggle: () => void;
}

const ThemeContext = createContext<Ctx | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }): JSX.Element {
  const [theme, setThemeState] = useState<Theme>(readInitial);
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);
  const setTheme = useCallback((t: Theme) => {
    setThemeState(t);
    try {
      localStorage.setItem(KEY, t);
    } catch {
      /* ignore */
    }
  }, []);
  const toggle = useCallback(() => {
    setThemeState((prev) => {
      const next = prev === "dark" ? "light" : "dark";
      try {
        localStorage.setItem(KEY, next);
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);
  const value = useMemo(() => ({ theme, setTheme, toggle }), [theme, setTheme, toggle]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): Ctx {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme outside ThemeProvider");
  return ctx;
}

export function ThemeSwitch(): JSX.Element {
  const { theme, toggle } = useTheme();
  const goingTo = theme === "dark" ? "light" : "dark";
  return (
    <div className="locale-switch">
      <button
        className="locale-btn on theme-btn"
        onClick={toggle}
        title={`Switch to ${goingTo} theme`}
        aria-label={`Switch to ${goingTo} theme`}
      >
        <Icon name={theme === "dark" ? "moon" : "sun"} />
      </button>
    </div>
  );
}
